/**
 * EMBED_PATH_PREFIX is the studio surface that only ever renders inside a
 * customer's own page, in a cross-origin iframe.
 */
const EMBED_PATH_PREFIX = "/studio/embed";

/**
 * CREDENTIAL_PATHS are the pages whose address carries a credential:
 *
 * - /studio/share, the public share surface, whose address carries the share token: a
 *   bearer credential that reads a private itinerary or collection until it is rotated, and
 *   that nothing can take back out of PostHog once a pageview or a replay has carried it
 *   there;
 * - /recovery and /verification, the account routes an email link opens with its one-time
 *   code in the address.
 */
const CREDENTIAL_PATHS = ["/studio/share", "/recovery", "/verification"];

/**
 * isAnalyticsExcludedPath reports whether a pathname is one no analytics or session replay
 * runs on: the embed surface and anything that starts with its path, which only ever errs
 * toward recording less, and each of the CREDENTIAL_PATHS with every path below it, matched
 * by segment so /studio/shared is not the share surface.
 */
export const isAnalyticsExcludedPath = (pathname: string): boolean =>
  pathname.startsWith(EMBED_PATH_PREFIX) ||
  CREDENTIAL_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));

/**
 * AnalyticsGate is everything the enablement decision reads. It is passed in
 * rather than read from `import.meta.env` so the rule stays a pure function the
 * test suite can drive directly.
 */
export interface AnalyticsGate {
  isE2E: boolean;
  isOptedOut: boolean;
  mode: string;
  pathname: string;
  token: string | undefined;
}

/**
 * shouldEnableAnalytics decides whether a surface may load PostHog at all.
 *
 * The path exclusions (isAnalyticsExcludedPath) are the substantive ones. /studio/embed
 * renders inside a third-party page, so its visitors are the customer's audience, not
 * ours, and recording them is not a call Mountaya gets to make. Cross-origin storage
 * partitioning would also hand out a fresh anonymous id per host, so the data would be
 * noise even setting the question of consent aside. The public share surface and the
 * account routes an emailed code opens carry a credential in their address, which a
 * pageview's URL and a replay would hand to PostHog, so they load no SDK either. A page
 * the SDK started on that later navigates to one of them is covered by the capture policy
 * instead (sanitizeCapture).
 *
 * The opt-out exclusion is what a device carries between visits. A person on an
 * excluded email domain is classified one round-trip after the SDK has already
 * started, so the decision is remembered and read back here: every later load
 * short-circuits before initAnalytics, and the posthog-js chunk is not even
 * fetched. It is remembered on the shared parent domain as well as per-origin,
 * which is what extends the exclusion to the documentation site — a property with
 * no identity of its own, and so no way to reach the decision itself.
 *
 * The remaining exclusions keep synthetic traffic out of the project: an unset
 * token (which is the local default), the Playwright suite, and the Vitest run.
 */
export const shouldEnableAnalytics = ({
  isE2E,
  isOptedOut,
  mode,
  pathname,
  token,
}: AnalyticsGate): boolean => {
  if (!token) {
    return false;
  }

  if (isOptedOut) {
    return false;
  }

  if (isE2E || mode === "test") {
    return false;
  }

  return !isAnalyticsExcludedPath(pathname);
};
